import {
  constants,
  Icon,
  Input,
  Radio,
  Select,
  Spin,
  Text,
} from "@findemdev/maui";
import type {
  ChangeEvent,
  Dispatch,
  ReactNode,
  SetStateAction,
} from "react";
import { Fragment, useCallback, useMemo } from "react";
import type { IExecProject } from "../../../../types/agent-pipelines";
import { CompanySelectOptionContent } from "../CompanyIdentity/CompanySelectOptionContent";
import {
  ProjectFormBody,
  ProjectFormAgentContextBadge,
  ProjectFormCancelButton,
  ProjectFormClientModeControl,
  ProjectFormControl,
  ProjectFormDropdownLoader,
  ProjectFormField,
  ProjectFormGroupLabel,
  ProjectFormHelpText,
  ProjectFormLabel,
  ProjectFormLabelRow,
  ProjectFormModalFrame,
  ProjectFormSubmitButton,
  ProjectFormText,
} from "../../RaasClientPortal.styled";
import {
  RaasProjectClientMode,
  type IFilterOption,
  type IProjectFormData,
  type ProjectFormFieldKey,
} from "../../types";
import type { IOrgV2 } from "../../../../types/orgs";
import { useProjectClientOrgOptions } from "./hooks/useProjectClientOrgOptions";
import { useProjectCompanyOptions } from "./hooks/useProjectCompanyOptions";
import { useProjectJobTitleOptions } from "./hooks/useProjectJobTitleOptions";

const { colors, dimensions } = constants;
const { Option } = Select;
const DROPDOWN_ICON_SIZE = Number.parseInt(dimensions.spacing["2"], 10);
const MODAL_CLOSE_ICON_SIZE =
  Number.parseInt(dimensions.spacing["2"], 10) +
  Number.parseInt(dimensions["border-radius"].xs, 10);
const PROJECT_FORM_MODAL_WIDTH =
  Number.parseInt(dimensions.spacing["10"], 10) * 7;

interface ICompanySelectOption {
  accountId?: string;
  domain?: string;
  label?: string;
  logo?: string;
  name?: string;
  value?: string;
}

interface IProjectFormModalProps {
  clientOrgs?: IOrgV2[];
  clientMode: RaasProjectClientMode;
  editingProject: IExecProject | null;
  fieldKeys: readonly ProjectFormFieldKey[];
  formData: IProjectFormData;
  isCreating: boolean;
  isFormValid: boolean;
  isUpdating: boolean;
  onClose: () => void;
  onClientModeChange: (clientMode: RaasProjectClientMode) => void;
  onSubmit: () => void;
  setFormData: Dispatch<SetStateAction<IProjectFormData>>;
  userOptions: IFilterOption[];
  visible: boolean;
}

interface IProjectFormDropdownRenderArgs {
  isLoading: boolean;
  menu: ReactNode;
}

const getCompanySelectOption = ({
  option,
}: {
  option: unknown;
}): ICompanySelectOption | undefined => {
  if (!option || Array.isArray(option) || typeof option !== "object") {
    return undefined;
  }

  return option as ICompanySelectOption;
};

const getCompanySelectionLabel = ({
  fallbackValue,
  option,
}: {
  fallbackValue: string;
  option?: ICompanySelectOption;
}): string => option?.label || option?.name || fallbackValue;

const renderProjectFormDropdown = ({
  isLoading,
  menu,
}: IProjectFormDropdownRenderArgs) => (
  <>
    {menu}
    {isLoading ? (
      <ProjectFormDropdownLoader>
        <Spin size="small" />
        <Text as="span">Fetching options</Text>
      </ProjectFormDropdownLoader>
    ) : null}
  </>
);

export const ProjectFormModal = ({
  clientOrgs,
  clientMode,
  editingProject,
  fieldKeys,
  formData,
  isCreating,
  isFormValid,
  isUpdating,
  onClose,
  onClientModeChange,
  onSubmit,
  setFormData,
  userOptions,
  visible,
}: IProjectFormModalProps) => {
  const hasClientField = fieldKeys.includes("client");
  const hasHiringForField = fieldKeys.includes("hiring_for");
  const isInternalCreate = hasClientField && !editingProject;
  const isCreate = !editingProject;
  const fallbackCompanyName =
    formData.client_name.trim() ||
    (hasClientField ? "the selected client" : "your organization");
  const shouldShowHiringFor =
    hasHiringForField &&
    (!isInternalCreate || clientMode === RaasProjectClientMode.Agency);
  const {
    isLoading: isClientOptionsLoading,
    options: clientOrgOptions,
    searchOrgs: searchClientOrgs,
  } = useProjectClientOrgOptions({
    orgs: clientOrgs,
    visible: visible && hasClientField,
  });
  const {
    isLoading: isHiringForOptionsLoading,
    options: hiringForCompanyOptions,
    searchCompanies: searchHiringForCompanies,
  } = useProjectCompanyOptions({ visible: visible && shouldShowHiringFor });
  const {
    isLoading: isJobTitleOptionsLoading,
    options: jobTitleOptions,
    searchJobTitles,
  } = useProjectJobTitleOptions({ visible });

  const clientSelectOptions = useMemo(() => {
    const options: ICompanySelectOption[] = clientOrgOptions.map((option) => ({
      accountId: option.accountId,
      domain: undefined,
      label: option.label,
      logo: undefined,
      name: option.name,
      value: option.ref,
    }));

    const selectedValue = formData.client_company_id || formData.client_name;
    if (
      selectedValue &&
      formData.client_name &&
      !options.some((option) => option.value === selectedValue)
    ) {
      options.unshift({
        accountId: formData.client_company_account_id || undefined,
        domain: undefined,
        label: formData.client_name,
        logo: undefined,
        name: formData.client_name,
        value: selectedValue,
      });
    }

    return options;
  }, [
    clientOrgOptions,
    formData.client_company_account_id,
    formData.client_name,
    formData.client_company_id,
  ]);

  const jobTitleSelectOptions = useMemo(() => {
    if (
      !formData.job_title ||
      jobTitleOptions.some((option) => option.value === formData.job_title)
    ) {
      return jobTitleOptions;
    }

    return [
      {
        label: formData.job_title,
        value: formData.job_title,
      },
      ...jobTitleOptions,
    ];
  }, [formData.job_title, jobTitleOptions]);

  const hiringForSelectOptions = useMemo(() => {
    const options = hiringForCompanyOptions.map((option) => ({
      domain: option.domain,
      label: option.label,
      logo: option.logo,
      name: option.name,
      value: option.ref,
    }));

    const selectedValue = formData.hiring_company_id || formData.hiring_company_name;
    if (
      selectedValue &&
      formData.hiring_company_name &&
      !options.some((option) => option.value === selectedValue)
    ) {
      options.unshift({
        domain: undefined,
        label: formData.hiring_company_name,
        logo: undefined,
        name: formData.hiring_company_name,
        value: selectedValue,
      });
    }

    return options;
  }, [
    formData.hiring_company_id,
    formData.hiring_company_name,
    hiringForCompanyOptions,
  ]);
  const assignedToSelectOptions = useMemo(() => {
    if (
      !formData.acting_user_id ||
      userOptions.some((option) => option.value === formData.acting_user_id)
    ) {
      return userOptions;
    }

    return [
      {
        label: formData.acting_user_id,
        value: formData.acting_user_id,
      },
      ...userOptions,
    ];
  }, [formData.acting_user_id, userOptions]);

  const handleDisplayNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        project_name: event.target.value,
      }));
    },
    [setFormData],
  );
  const handleClientChange = useCallback(
    ({
      option,
      selectedValue,
    }: {
      option: unknown;
      selectedValue: string;
    }) => {
      const selectedOption = getCompanySelectOption({ option });

      setFormData((prev) => ({
        ...prev,
        client_name: getCompanySelectionLabel({
          fallbackValue: selectedValue,
          option: selectedOption,
        }),
        client_company_account_id: selectedOption?.accountId || "",
        client_company_id: selectedValue,
        client_domain: selectedOption?.domain || "",
      }));
    },
    [setFormData],
  );
  const handleClientSearch = useCallback(
    (searchValue: string) => {
      setFormData((prev) => ({
        ...prev,
        client_company_account_id: "",
        client_name: searchValue,
        client_company_id: "",
        client_domain: "",
      }));
      searchClientOrgs({ searchValue });
    },
    [searchClientOrgs, setFormData],
  );
  const handleJobTitleSearch = useCallback(
    (searchValue: string) => {
      setFormData((prev) => ({
        ...prev,
        job_title: searchValue,
      }));
      searchJobTitles({ searchValue });
    },
    [searchJobTitles, setFormData],
  );
  const handleJobTitleChange = useCallback(
    (selectedValue: string) => {
      setFormData((prev) => ({
        ...prev,
        job_title: selectedValue,
      }));
    },
    [setFormData],
  );
  const handleAssignedToChange = useCallback(
    (selectedValue: unknown) => {
      setFormData((prev) => ({
        ...prev,
        acting_user_id: typeof selectedValue === "string" ? selectedValue : "",
      }));
    },
    [setFormData],
  );
  const handleHiringForSearch = useCallback(
    (searchValue: string) => {
      searchHiringForCompanies({ searchValue });
    },
    [searchHiringForCompanies],
  );
  const handleHiringForChange = useCallback(
    ({
      option,
      selectedValue,
    }: {
      option: unknown;
      selectedValue: string;
    }) => {
      const selectedOption = getCompanySelectOption({ option });

      setFormData((prev) => ({
        ...prev,
        hiring_company_id: selectedValue,
        hiring_company_name: getCompanySelectionLabel({
          fallbackValue: selectedValue,
          option: selectedOption,
        }),
      }));
    },
    [setFormData],
  );
  const handleHiringForClear = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      hiring_company_id: "",
      hiring_company_name: "",
    }));
  }, [setFormData]);

  const renderProjectFormField = (fieldKey: ProjectFormFieldKey) => {
    switch (fieldKey) {
      case "project_name":
        return (
          <ProjectFormField>
            <ProjectFormText>
              <ProjectFormLabel htmlFor="raas-project-name">
                {isInternalCreate ? "Project name" : "Project Name"}
              </ProjectFormLabel>
            </ProjectFormText>
            <ProjectFormControl>
              <Input
                debounceDelay={0}
                id="raas-project-name"
                onChange={handleDisplayNameChange}
                placeholder={
                  isInternalCreate
                    ? "VP Engineering, Acme Corp"
                    : "e.g., VP Engineering, Acme Corp"
                }
                value={formData.project_name}
              />
            </ProjectFormControl>
          </ProjectFormField>
        );
      case "job_title":
        return (
          <ProjectFormField>
            <ProjectFormText>
              <ProjectFormLabel htmlFor="raas-project-job-title">
                {isInternalCreate ? "Job title" : "Job Title"}
              </ProjectFormLabel>
            </ProjectFormText>
            <ProjectFormControl>
              <Select
                filterOption={false}
                dropdownRender={(menu) =>
                  renderProjectFormDropdown({
                    isLoading: isJobTitleOptionsLoading,
                    menu,
                  })
                }
                id="raas-project-job-title"
                loading={isJobTitleOptionsLoading}
                notFoundContent={
                  isJobTitleOptionsLoading ? "Loading..." : "Type to search"
                }
                onChange={(value) => handleJobTitleChange(String(value))}
                onSearch={handleJobTitleSearch}
                options={jobTitleSelectOptions}
                placeholder={
                  isInternalCreate ? "VP Engineering" : "e.g., VP Engineering"
                }
                showSearch
                suffixIcon={
                  <Icon
                    icon="expand_more"
                    $size={DROPDOWN_ICON_SIZE}
                    $color={colors["grey-450"]}
                  />
                }
                value={formData.job_title || undefined}
              />
            </ProjectFormControl>
            {isInternalCreate ? (
              <ProjectFormHelpText>Required</ProjectFormHelpText>
            ) : null}
          </ProjectFormField>
        );
      case "acting_user_id":
        if (!editingProject) return null;

        return (
          <ProjectFormField>
            <ProjectFormText>
              <ProjectFormLabel htmlFor="raas-project-assigned-to">
                Assigned To
              </ProjectFormLabel>
            </ProjectFormText>
            <ProjectFormControl>
              <Select
                id="raas-project-assigned-to"
                onChange={handleAssignedToChange}
                optionFilterProp="label"
                options={assignedToSelectOptions}
                placeholder="Select a user"
                showSearch
                suffixIcon={
                  <Icon
                    icon="expand_more"
                    $size={DROPDOWN_ICON_SIZE}
                    $color={colors["grey-450"]}
                  />
                }
                value={formData.acting_user_id || undefined}
              />
            </ProjectFormControl>
          </ProjectFormField>
        );
      case "client":
        return (
          <ProjectFormField>
            <ProjectFormText>
              <ProjectFormLabelRow>
                <ProjectFormLabel htmlFor="raas-project-client">
                  Client
                </ProjectFormLabel>
                {isInternalCreate &&
                clientMode === RaasProjectClientMode.Enterprise ? (
                  <ProjectFormAgentContextBadge>
                    Agent context source
                  </ProjectFormAgentContextBadge>
                ) : null}
              </ProjectFormLabelRow>
              <ProjectFormHelpText>
                {isInternalCreate
                  ? clientMode === RaasProjectClientMode.Enterprise
                    ? "The company you're hiring for"
                    : "The agency this project is for"
                  : "The executive search firm this project is for"}
              </ProjectFormHelpText>
            </ProjectFormText>
            <ProjectFormControl>
              <Select
                filterOption={false}
                dropdownRender={(menu) =>
                  renderProjectFormDropdown({
                    isLoading: isClientOptionsLoading,
                    menu,
                  })
                }
                id="raas-project-client"
                loading={isClientOptionsLoading}
                notFoundContent={
                  isClientOptionsLoading ? "Loading..." : "Type to search"
                }
                optionLabelProp="label"
                onChange={(value, option) =>
                  handleClientChange({
                    option,
                    selectedValue: String(value),
                  })
                }
                onSearch={handleClientSearch}
                placeholder="Select a client"
                showSearch
                suffixIcon={
                  <Icon
                    icon="expand_more"
                    $size={DROPDOWN_ICON_SIZE}
                    $color={colors["grey-450"]}
                  />
                }
                value={
                  formData.client_company_id || formData.client_name || undefined
                }
              >
                {clientSelectOptions.map((option) => (
                  <Option
                    accountId={option.accountId}
                    domain={option.domain}
                    key={option.value}
                    label={option.label}
                    logo={option.logo}
                    name={option.name}
                    value={option.value}
                  >
                    <CompanySelectOptionContent
                      label={option.label || option.name || ""}
                      logoRef={option.logo}
                    />
                  </Option>
                ))}
              </Select>
            </ProjectFormControl>
          </ProjectFormField>
        );
      case "hiring_for":
        if (!shouldShowHiringFor) return null;

        return (
          <ProjectFormField>
            <ProjectFormText>
              <ProjectFormLabelRow>
                <ProjectFormLabel htmlFor="raas-project-hiring-for">
                  {isInternalCreate ? "Hiring for" : "Hiring for (optional)"}
                </ProjectFormLabel>
                {isInternalCreate ? (
                  <ProjectFormAgentContextBadge>
                    Agent context source
                  </ProjectFormAgentContextBadge>
                ) : null}
              </ProjectFormLabelRow>
              <ProjectFormHelpText>
                {isInternalCreate
                  ? "The company the client is hiring for"
                  : isCreate
                  ? `The company this search is for. Leave blank to use ${fallbackCompanyName}.`
                  : "The company the client is hiring for"}
              </ProjectFormHelpText>
            </ProjectFormText>
            <ProjectFormControl>
              <Select
                allowClear={!isInternalCreate}
                filterOption={false}
                dropdownRender={(menu) =>
                  renderProjectFormDropdown({
                    isLoading: isHiringForOptionsLoading,
                    menu,
                  })
                }
                id="raas-project-hiring-for"
                loading={isHiringForOptionsLoading}
                notFoundContent={
                  isHiringForOptionsLoading ? "Loading..." : "Type to search"
                }
                optionLabelProp="label"
                onChange={(value, option) => {
                  if (value === undefined || value === null || value === "") {
                    handleHiringForClear();
                    return;
                  }

                  handleHiringForChange({
                    option,
                    selectedValue: String(value),
                  });
                }}
                onSearch={handleHiringForSearch}
                placeholder="Select a company"
                showSearch
                suffixIcon={
                  <Icon
                    icon="expand_more"
                    $size={DROPDOWN_ICON_SIZE}
                    $color={colors["grey-450"]}
                  />
                }
                value={
                  formData.hiring_company_id || formData.hiring_company_name || undefined
                }
              >
                {hiringForSelectOptions.map((option) => (
                  <Option
                    domain={option.domain}
                    key={option.value}
                    label={option.label}
                    logo={option.logo}
                    name={option.name}
                    value={option.value}
                  >
                    <CompanySelectOptionContent
                      label={option.label || option.name || ""}
                      logoRef={option.logo}
                    />
                  </Option>
                ))}
              </Select>
            </ProjectFormControl>
          </ProjectFormField>
        );
      default:
        return null;
    }
  };

  return (
    <ProjectFormModalFrame
      $isInternalCreate={isInternalCreate}
      closeIcon={
        <Icon
          icon="close"
          $size={MODAL_CLOSE_ICON_SIZE}
          $color={colors["grey-500"]}
        />
      }
      footer={[
        <ProjectFormCancelButton key="cancel" onClick={onClose}>
          Cancel
        </ProjectFormCancelButton>,
        <ProjectFormSubmitButton
          disabled={!isFormValid}
          key="save"
          loading={isCreating || isUpdating}
          onClick={onSubmit}
          type="primary"
        >
          {editingProject ? "Save Changes" : "Create"}
        </ProjectFormSubmitButton>,
      ]}
      onCancel={onClose}
      title={
        editingProject
          ? "Edit Project"
          : isInternalCreate
            ? "Create project"
            : "Create Project"
      }
      visible={visible}
      width={PROJECT_FORM_MODAL_WIDTH}
    >
      <ProjectFormBody>
        {isInternalCreate ? (
          <ProjectFormField>
            <ProjectFormText>
              <ProjectFormGroupLabel id="raas-project-client-mode-label">
                Client type
              </ProjectFormGroupLabel>
            </ProjectFormText>
            <ProjectFormClientModeControl
              aria-labelledby="raas-project-client-mode-label"
              role="radiogroup"
            >
              <Radio.Group
                buttonStyle="solid"
                name="raas-project-client-mode"
                onChange={(event) =>
                  onClientModeChange(
                    event.target.value as RaasProjectClientMode,
                  )
                }
                optionType="button"
                value={clientMode}
              >
                <Radio.Button value={RaasProjectClientMode.Enterprise}>
                  <Text as="span">Enterprise</Text>
                </Radio.Button>
                <Radio.Button value={RaasProjectClientMode.Agency}>
                  <Text as="span">Agency</Text>
                </Radio.Button>
              </Radio.Group>
            </ProjectFormClientModeControl>
          </ProjectFormField>
        ) : null}
        {fieldKeys.map((fieldKey) => (
          <Fragment key={fieldKey}>
            {renderProjectFormField(fieldKey)}
          </Fragment>
        ))}
      </ProjectFormBody>
    </ProjectFormModalFrame>
  );
};
